import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { toggleTeamModal, toggleSelectTeamOwnerModal, showMessageModal } from '../../Manage/actions'
import styles from './JoinTeamBanner.module.css'
import { requestToJoinTeam } from 'services/manage'
import { PLANS } from 'constants/plans'

const JoinTeamBanner = ({ userProfile }) => {
  const [show, setShow] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()

  const { potentialTeamOwners, potentialTeamMembers, productId, parentEmail } = userProfile || {}

  const isFreeUser = !productId
  const isProUser = [PLANS.PRO_U.id, PLANS.PRO_MAX.id].includes(productId)
  const isChildAccount = !!parentEmail

  let bannerType = null
  let teamData = []

  if (isFreeUser && potentialTeamOwners?.length > 0 && !isChildAccount) {
    bannerType = 'join'
    teamData = potentialTeamOwners
  } else if (isProUser && potentialTeamMembers?.length > 0) {
    bannerType = 'invite'
    teamData = potentialTeamMembers
  }

  // Don't show banner if no relevant data or already dismissed
  if (!bannerType || !show || localStorage.getItem(`${bannerType}TeamBannerClosed`) === 'true') {
    return null
  }

  const handleClose = (e) => {
    e.preventDefault()
    e.stopPropagation()
    localStorage.setItem(`${bannerType}TeamBannerClosed`, 'true')
    setShow(false)
  }

  const handlePrimaryAction = async () => {
    if (bannerType === 'join') {
      // If multiple team owners, show selection modal
      if (teamData.length > 1) {
        dispatch(toggleSelectTeamOwnerModal({ show: true, teamOwners: teamData }))
        return
      }

      // For single team owner, check if request already pending
      const primaryOwner = teamData[0]
      if (primaryOwner.requestStatus === 'pending') {
        return
      }

      setIsLoading(true)
      try {
        await requestToJoinTeam(primaryOwner.email)

        // Show success message
        dispatch(
          showMessageModal({
            title: 'Request Sent Successfully!',
            message:
              'Your request to join the team has been sent. The team owner will receive an email notification to approve your request.',
            error: false
          })
        )

        setShow(false)
      } catch (error) {
        console.error('Failed to request team join:', error)

        // Show error message
        dispatch(
          showMessageModal({
            title: 'Request Failed',
            message: 'Failed to send your team join request. Please try again.',
            error: true
          })
        )
      } finally {
        setIsLoading(false)
      }
    } else if (bannerType === 'invite') {
      dispatch(toggleTeamModal(true))
    }
  }

  const config = {
    join: {
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      buttonColor: 'white',
      title: 'Join your team on Tiiny Host',
      subtitle: (() => {
        const firstOwnerEmail = teamData[0].email
        return teamData.length === 1
          ? `${firstOwnerEmail} has a Pro account with your company domain`
          : `${firstOwnerEmail} has a Pro account with your company domain (+${teamData.length - 1} more)`
      })(),
      buttonText: (() => {
        if (isLoading) return 'Requesting...'
        if (teamData.length > 1) return 'Choose team owner'

        // For single team owner, check request status
        const primaryOwner = teamData[0]
        if (primaryOwner.requestStatus === 'pending') return 'Requested'
        if (primaryOwner.requestStatus === 'denied') return 'Request denied'
        return 'Request to join'
      })(),
      buttonDisabled: isLoading,
      actionButtonStyle: {
        backgroundColor: 'transparent',
        color: 'white',
        border: '1px solid white'
      },
      closeButtonImage: '/assets/icons/cancel_white.svg'
    },
    invite: {
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      buttonColor: '#11998e',
      title: 'Invite your team to collaborate',
      subtitle:
        teamData.length === 1
          ? `${teamData[0].email} from your company is using Tiiny Host`
          : `${teamData.length} colleagues from your company are using Tiiny Host`,
      buttonText: 'Invite team',
      buttonDisabled: false,
      actionButtonStyle: {
        backgroundColor: 'white',
        color: '#11998e',
        border: 'none'
      },
      closeButtonImage: '/assets/icons/cancel_white.svg'
    }
  }

  const currentConfig = config[bannerType]

  return (
    <div className={styles.bannerContainer} style={{ background: currentConfig.gradient }}>
      <div className={styles.bannerContent}>
        <button className={styles.closeButton} onClick={handleClose}>
          <img src={currentConfig.closeButtonImage} height="15" width="15" alt="close" />
        </button>

        <div className={styles.contentWrapper}>
          <div className={styles.iconSection}>
            <img
              src="/assets/icons/team.svg"
              width="24"
              height="24"
              alt="Team"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>

          <div className={styles.textSection}>
            <h4 className={styles.title}>{currentConfig.title}</h4>
            <p className={styles.subtitle}>{currentConfig.subtitle}</p>
          </div>

          <div className={styles.buttonSection}>
            <Button
              variant="primary"
              size="sm"
              onClick={handlePrimaryAction}
              disabled={currentConfig.buttonDisabled}
              style={currentConfig.actionButtonStyle}
            >
              {currentConfig.buttonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JoinTeamBanner
